/**
 * Bill Repository Implementation
 * Implements the BillRepository interface
 * Coordinates between API calls and mappers
 */

import { BillRepository } from "../../domain/repositories/BillRepository";
import {
  BillEntity,
  BillsResultEntity,
  BillOperationResult,
  BillDataEntity,
  BillStatus,
  BillListStatus,
  BillReviewStatus,
  BillCountsEntity,
  ManualBillCreatedEntity,
} from "../../domain/entities/BillEntity";
import {
  fetchBills,
  getBill,
  updateBillJson,
  updateBillStatus,
  processBillJson,
  createZohoBill,
  updateZohoBill,
  createManualBill,
  markBillAsDuplicate,
  rejectBill,
  deletePostedBill,
  fetchBillCounts,
} from "../api/billsApi";
import { BillMapper } from "../mappers/BillMapper";
import type { ApprovalAuditPayload } from "../../../../shared/utils/reviewWorkflow";

export class BillRepositoryImpl implements BillRepository {
  /**
   * Fetch all bills with optional filtering
   */
  async fetchBills(params?: {
    limit?: number;
    offset?: number;
    status?: BillStatus | BillListStatus;
    reviewed?: BillReviewStatus;
    search?: string;
    companyid?: string;
    date_from?: string;
    date_to?: string;
    created_after?: string;
    created_before?: string;
    sort_by?:
      | "created_at"
      | "updated_at"
      | "filename"
      | "status"
      | "original_filename";
    sort_order?: "asc" | "desc";
  }): Promise<BillsResultEntity> {
    const response = await fetchBills(params);
    return BillMapper.toBillsResultEntity(response);
  }

  /**
   * Get a single bill by ID
   */
  async getBill(billId: string): Promise<BillEntity> {
    const billDTO = await getBill(billId);
    return BillMapper.toBillEntity(billDTO);
  }

  /**
   * Update bill JSON data
   */
  async updateBillJson(
    billId: string,
    billData: BillDataEntity
  ): Promise<BillOperationResult> {
    // Convert entity to DTO
    const billDataDTO = BillMapper.toBillDataDTO(billData);

    // Call API
    const billDTO = await updateBillJson(billId, billDataDTO);

    // Map back to entity and return operation result
    return {
      success: true,
      message: "Bill JSON updated successfully",
      bill: BillMapper.toBillEntity(billDTO),
    };
  }

  /**
   * Update bill status
   */
  async updateBillStatus(
    billId: string,
    status: BillStatus
  ): Promise<BillOperationResult> {
    const response = await updateBillStatus(billId, parseInt(status));
    return BillMapper.toBillOperationResult(response);
  }

  /**
   * Process bill JSON for GL classification
   */
  async processBillJson(
    billData: BillDataEntity
  ): Promise<BillOperationResult> {
    // Convert entity to DTO
    const billDataDTO = BillMapper.toBillDataDTO(billData);

    // Call API
    const response = await processBillJson(billDataDTO);

    return BillMapper.toBillOperationResult(response);
  }

  /**
   * Create bill in Zoho Books
   */
  async createZohoBill(
    billId: string,
    billData: BillDataEntity,
    pdfUrl: string,
    approvalAudit?: ApprovalAuditPayload,
  ): Promise<BillOperationResult> {
    const billDataDTO = BillMapper.toBillDataDTO(billData);
    const response = await createZohoBill(
      billId,
      billDataDTO,
      pdfUrl,
      approvalAudit,
    );
    return BillMapper.toBillOperationResult(response);
  }

  async updateZohoBill(
    billId: string,
    billData: BillDataEntity,
    approvalAudit?: ApprovalAuditPayload,
  ): Promise<BillOperationResult> {
    const billDataDTO = BillMapper.toBillDataDTO(billData);
    const response = await updateZohoBill(billId, billDataDTO, approvalAudit);
    return BillMapper.toBillOperationResult(response);
  }

  /**
   * Mark bill as duplicate
   */
  async markBillAsDuplicate(billId: number): Promise<BillOperationResult> {
    const response = await markBillAsDuplicate(billId);
    return BillMapper.toBillOperationResult(response);
  }

  /**
   * Reject a bill
   */
  async rejectBill(billId: number): Promise<BillOperationResult> {
    const response = await rejectBill(billId);
    return BillMapper.toBillOperationResult(response);
  }

  /**
   * Delete a posted bill from Zoho and soft-delete locally
   */
  async deletePostedBill(billId: number): Promise<BillOperationResult> {
    const response = await deletePostedBill(billId);
    return BillMapper.toBillOperationResult(response);
  }

  /**
   * Fetch bill counts for dashboard cards
   */
  async fetchBillCounts(params?: {
    date_from?: string;
    date_to?: string;
  }): Promise<BillCountsEntity> {
    const response = await fetchBillCounts(params);
    return {
      pendingCount: Number(response.pending_count) || 0,
      onHoldCount: Number(response.on_hold_count) || 0,
      postedCount: Number(response.posted_count) || 0,
      duplicateCount: Number(response.duplicate_count) || 0,
      rejectedCount: Number(response.rejected_count) || 0,
      rejectedDuplicateCount: Number(response.rejected_duplicate_count) || 0,
      underReviewCount: Number(response.under_review_count) || 0,
    };
  }

  /**
   * Create manual bill (form data + optional attachments, then push to Zoho)
   */
  async createManualBill(
    billData: BillDataEntity,
    companyGstEnabled: number,
    attachments?: File[]
  ): Promise<ManualBillCreatedEntity> {
    const billDataDTO = BillMapper.toBillDataDTO(billData);
    const response = await createManualBill(
      billDataDTO,
      companyGstEnabled,
      attachments
    );
    const data = response.data;
    if (!data?.id) {
      throw new Error(response.message || "Create manual bill failed");
    }
    return {
      id: data.id,
      zohoBillId: data.zoho_bill_id,
      billNumber: data.bill_number,
    };
  }
}
